import { FileSourceEnum } from '@systemeio/file-manager'
import { advancedTextEditorExtensions, TipTapEditor } from '@systemeio/tip-tap'
import { Hint, LocaleEnum, Popover } from '@systemeio/ui'
import { EditablePaymentSettings } from 'modules/profile/payment/types/payment-settings-interface'
import React, { useState } from 'react'
import FormTextarea from 'shared/components/form-textarea'
import { LanguageSwitcher } from 'shared/components/language-switcher/language-switcher'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { QuestionMarkIcon } from '@systemeio/ui/icons'
import { twJoin } from 'tailwind-merge'

interface AgreementEditorProps {
  tempState: EditablePaymentSettings
  setTempState: React.Dispatch<React.SetStateAction<EditablePaymentSettings>>
  locale: LocaleEnum
  errors: Record<LocaleEnum, string>
}

function AgreementEditor({ setTempState, tempState, locale, errors }: AgreementEditorProps) {
  const { t } = useLocoTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState(locale)

  return (
    <div className="lg:w-2/3">
      <div className="[&>svg]:fill-brand-primary [&>svg]:main-transition-colors mb-2 flex items-center gap-2">
        {t('dashboard.settings.payment_page.agreement_title')}
        <Popover
          label={t('dashboard.settings.payment_page.agreement_popover_title')}
          popoverClassName={'w-[200px] sm:w-[350px]'}
        >
          {show => (
            <QuestionMarkIcon
              className={`${
                show ? 'fill-brand-primary' : 'fill-fill-primary'
              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
            />
          )}
        </Popover>
        <div className="ml-auto">
          <LanguageSwitcher
            changeLanguage={setSelectedLanguage}
            selectedLanguage={selectedLanguage}
          />
        </div>
      </div>
      <TipTapEditor
        content={tempState.paymentPageAgreement[selectedLanguage]}
        onChange={data =>
          setTempState(prev => {
            prev.paymentPageAgreement[selectedLanguage] = data
            return prev
          })
        }
        className={twJoin(!!errors[selectedLanguage] && 'border-red')}
        fileSource={FileSourceEnum.Settings}
        extensions={advancedTextEditorExtensions}
      />
      {errors[selectedLanguage] && (
        <p className="text-danger text-sm">{errors[selectedLanguage]}</p>
      )}
      <div className="pt-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <Hint label={t('dashboard.user_settings.payment_page.popover_sales_invoice_footer')}>
            {t('dashboard.user_settings.payment_page.sales_invoice_footer')}
          </Hint>
          <LanguageSwitcher
            changeLanguage={setSelectedLanguage}
            selectedLanguage={selectedLanguage}
          />
        </div>
        <FormTextarea
          placeholder={t('dashboard.user_settings.payment_page.sales_invoice_footer')}
          value={tempState.salesInvoiceFooter[selectedLanguage] || ''}
          onChange={e =>
            setTempState(prevState => ({
              ...prevState,
              salesInvoiceFooter: {
                ...prevState.salesInvoiceFooter,
                [selectedLanguage]: e.target.value,
              },
            }))
          }
        />
      </div>
    </div>
  )
}

export default AgreementEditor
